/**
 * Battery status scale — RVS battery ONLY.
 *
 * This is deliberately NOT the machine-health scale. Machine health
 * (overAllSensorStatus) remains -1/0/1/2 and must keep using the
 * `colorStatus` mixin / per-component maps. Do not merge the two.
 *
 * Source of truth: rvs_model_result_data_summary_status_sensor.batteryStatus,
 * written by analytics. Voltage is display-only and must never be used to
 * derive a status.
 */
export const BATTERY_STATUS = {
  OFFLINE: -1,
  NORMAL: 0,
  WARNING: 1,
  ALARM: 2,
  CRITICAL: 3,
} as const;

export type BatteryStatusCode = (typeof BATTERY_STATUS)[keyof typeof BATTERY_STATUS];

export interface BatteryStatusMeta {
  /** null = no row in the summary table */
  code: BatteryStatusCode | null;
  /** slug: API query-param value AND css class suffix (`bg-status-${key}`) */
  key: string;
  label: string;
  /** severity ramp hex */
  color: string;
  /** readable foreground on `color` */
  textColor: string;
  recommendation: string;
}

export const BATTERY_STATUS_NO_DATA: BatteryStatusMeta = {
  code: null,
  key: "no_data",
  label: "No Data",
  color: "#808080",
  textColor: "#FFFFFF",
  recommendation: "-",
};

/** Ordered most-severe first — drives filter dropdowns and rollup column order. */
export const BATTERY_STATUS_LIST: BatteryStatusMeta[] = [
  {
    code: BATTERY_STATUS.CRITICAL,
    key: "critical",
    label: "Critical",
    color: "#8B0000",
    textColor: "#FFFFFF",
    recommendation: "Change battery within 2 weeks",
  },
  {
    code: BATTERY_STATUS.ALARM,
    key: "alarm",
    label: "Alarm",
    color: "#D9263B",
    textColor: "#FFFFFF",
    recommendation: "Estimate 2 months, Change battery",
  },
  {
    code: BATTERY_STATUS.WARNING,
    key: "warning",
    label: "Warning",
    color: "#FCA239",
    textColor: "#FFFFFF",
    recommendation: "Estimate 3 months, Prepare battery",
  },
  {
    code: BATTERY_STATUS.NORMAL,
    key: "normal",
    label: "Normal",
    color: "#2EC577",
    textColor: "#FAFAFA",
    recommendation: "-",
  },
  {
    code: BATTERY_STATUS.OFFLINE,
    key: "offline",
    label: "Offline",
    color: "#A5ACB3",
    textColor: "#FFFFFF",
    recommendation: "-",
  },
  BATTERY_STATUS_NO_DATA,
];

const BY_CODE: Record<string, BatteryStatusMeta> = BATTERY_STATUS_LIST.reduce(
  (acc, m) => (m.code === null ? acc : { ...acc, [String(m.code)]: m }),
  {}
);

export function batteryStatusMeta(code: number | null | undefined): BatteryStatusMeta {
  if (code === null || code === undefined) return BATTERY_STATUS_NO_DATA;
  return BY_CODE[String(code)] || BATTERY_STATUS_NO_DATA;
}

export const batteryStatusLabel = (c: number | null | undefined) => batteryStatusMeta(c).label;
export const batteryStatusKey = (c: number | null | undefined) => batteryStatusMeta(c).key;
export const batteryStatusColor = (c: number | null | undefined) => batteryStatusMeta(c).color;
export const batteryStatusRecommendation = (c: number | null | undefined) => batteryStatusMeta(c).recommendation;

/** Inline style for a pill badge — avoids Tailwind JIT safelisting of dynamic classes. */
export function batteryBadgeStyle(code: number | null | undefined) {
  const m = batteryStatusMeta(code);
  return { backgroundColor: m.color, color: m.textColor };
}

/** Dropdown items for the Battery Status filter, with an "All" head. */
export const BATTERY_FILTER_OPTIONS = [
  { label: "All Battery", value: null as string | null },
  ...BATTERY_STATUS_LIST.map((m) => ({ label: m.label, value: m.key })),
];

/** Count columns for the Battery-by-Company rollup, Critical-first. */
export const BATTERY_ROLLUP_COLUMNS = BATTERY_STATUS_LIST.filter((m) => m.key !== "no_data");
